/**
 * Écran « Alertes » et bloc d'alertes réutilisable par le tableau de bord.
 *
 * Une alerte affichée dit trois choses : ce qui est observé, contre quel seuil,
 * et ce qu'il y a à faire. Les seuils viennent du moteur, jamais d'ici.
 */

import {
  entier,
  euros,
  libelleDomaine,
  libelleGravite,
  nombre,
  pourcent,
  type Alerte,
  type DomaineAlerte,
  type Gravite,
} from '@scc/core';
import type { Charge } from '../api.js';
import { h } from '../dom.js';
import { badge, enteteSection, noteLecture } from '../composants.js';

/** Rend une valeur d'alerte dans son unité. */
function valeur(a: Alerte): string {
  switch (a.unite) {
    case 'euros':
      return euros(a.valeur);
    case 'ratio':
      return pourcent(a.valeur, 0);
    case 'heures':
      return `${entier(a.valeur)} h`;
    case 'nombre':
      return entier(a.valeur);
  }
}

function seuilsLisibles(a: Alerte): string {
  const f = (n: number): string =>
    a.unite === 'ratio' ? pourcent(n, 0) : a.unite === 'heures' ? `${entier(n)} h` : nombre(n, 2);
  return `vigilance à ${f(a.seuils.vigilance)}, alerte à ${f(a.seuils.alerte)}`;
}

const CLASSE: Record<Gravite, string> = {
  alerte: 'alerte g-alerte',
  vigilance: 'alerte g-vigilance',
  nominal: 'alerte g-nominal',
};

const NIVEAU: Record<Gravite, 'favorable' | 'vigilance' | 'alerte'> = {
  nominal: 'favorable',
  vigilance: 'vigilance',
  alerte: 'alerte',
};

/** Une alerte, rendue en bloc. */
export function blocAlerte(a: Alerte): HTMLElement {
  return h(
    'article',
    { classe: CLASSE[a.gravite], 'aria-label': `${libelleGravite(a.gravite)} — ${a.libelle}` },
    h('div', { classe: 'bande', 'aria-hidden': 'true' }),
    h(
      'div',
      { classe: 'corps' },
      h('p', { classe: 'titre-alerte' }, a.libelle),
      h('p', { classe: 'constat' }, a.constat),
      a.conduite !== '' ? h('p', { classe: 'conduite' }, a.conduite) : null,
    ),
    h(
      'div',
      { classe: 'etiquette' },
      badge(NIVEAU[a.gravite], libelleGravite(a.gravite)),
      h('p', { classe: 'sous-chiffre' }, valeur(a)),
    ),
  );
}

/** Liste d'alertes, filtrée sur les gravités demandées. */
export function listeAlertes(
  alertes: readonly Alerte[],
  gravites: readonly Gravite[],
): HTMLElement {
  const retenues = alertes.filter((a) => gravites.includes(a.gravite));
  if (retenues.length === 0) {
    return h(
      'div',
      { classe: 'insight positive' },
      h('span', { classe: 'insight-icon', 'aria-hidden': 'true' }, '✦'),
      h('p', {}, 'Aucune règle n’est en dépassement. Rien n’appelle de décision immédiate.'),
    );
  }
  return h('div', { classe: 'liste-alertes' }, ...retenues.map(blocAlerte));
}

export function vueAlertes(d: Charge): HTMLElement {
  const parDomaine = new Map<DomaineAlerte, Alerte[]>();
  for (const a of d.alertes) {
    const l = parDomaine.get(a.domaine) ?? [];
    l.push(a);
    parDomaine.set(a.domaine, l);
  }

  return h(
    'div',
    { classe: 'page', id: 'page-alertes', role: 'region', 'aria-label': 'Alertes' },
    h(
      'div',
      { classe: 'entete-page' },
      h(
        'div',
        {},
        h('h1', {}, 'Alertes'),
        h(
          'p',
          { classe: 'chapeau' },
          `${entier(d.compteurAlertes.alerte)} alerte${d.compteurAlertes.alerte > 1 ? 's' : ''}, ` +
            `${entier(d.compteurAlertes.vigilance)} point${d.compteurAlertes.vigilance > 1 ? 's' : ''} de vigilance, ` +
            `${entier(d.compteurAlertes.nominal)} règle${d.compteurAlertes.nominal > 1 ? 's' : ''} nominale${d.compteurAlertes.nominal > 1 ? 's' : ''}. ` +
            `Chaque règle porte son seuil et la conduite à tenir.`,
        ),
      ),
    ),
    ...[...parDomaine.entries()].map(([domaine, liste]) =>
      h(
        'section',
        {},
        enteteSection(
          [...parDomaine.keys()].indexOf(domaine) + 1,
          libelleDomaine(domaine),
          `${liste.length} règle${liste.length > 1 ? 's' : ''} évaluée${liste.length > 1 ? 's' : ''}`,
        ),
        h('div', { classe: 'liste-alertes' }, ...liste.map(blocAlerte)),
        h(
          'div',
          { classe: 'barre-actions' },
          h('span', { classe: 'titre' }, 'Seuils appliqués'),
          ...liste.map((a) => h('span', { classe: 'sous-chiffre' }, `${a.libelle} — ${seuilsLisibles(a)}`)),
        ),
      ),
    ),
    noteLecture(
      'Les seuils sont déclarés dans un fichier de configuration versionné. Les modifier vaut ' +
        'décision de gestion : le changement se relit dans l’historique du dépôt.',
    ),
  );
}
